// frontend/app/components/lessonPlans/authoring/BlockPalette.js
//
// What an author can add to a step, grouped by what the block is *for* rather
// than by how it is rendered.
//
// Two things are on every chip that used to be invisible until a lesson was
// live: the Loop phase the type belongs to, and what one answer weighs towards
// mastery. A `reflection` dropped into Wonder and a lesson whose only tagged
// blocks are predictions both look perfectly fine in an editor that does not
// say so.

import React from "react";
import { StyleSheet, Text, TouchableOpacity, View } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";

import { BLOCK_GROUPS, BLOCK_SCHEMAS, evidenceLevelFor } from "./blockSchemas";
import { getSectionForPhase, getSectionMeta } from "../lessonPlanTheme";

const PaletteChip = ({ colors, schema, stepPhase, onAdd, t }) => {
  const translate = t || ((key) => key);
  const homeSection = schema.phase ? getSectionForPhase(schema.phase) : null;
  const phase = homeSection ? getSectionMeta(homeSection) : null;
  const level = evidenceLevelFor(schema.evidenceWeight);
  // A type with no home phase (a paragraph, a 3D scene) belongs anywhere, so
  // only a type that names one can be out of place.
  // Compared by section: phases are never shown, so two phases of one section
  // are the same place to an author.
  const elsewhere = Boolean(
    homeSection && stepPhase && homeSection !== getSectionForPhase(stepPhase)
  );

  return (
    <TouchableOpacity
      onPress={() => onAdd(schema.type)}
      accessibilityRole="button"
      accessibilityLabel={`${translate("Add")} ${translate(schema.label)}`}
      style={[
        styles.chip,
        {
          backgroundColor: colors.panelBg,
          borderColor: elsewhere ? colors.borderStrong : colors.border,
          opacity: elsewhere ? 0.75 : 1,
        },
      ]}
    >
      <MaterialCommunityIcons name={schema.icon} size={14} color={colors.accentPrimary} />
      <View style={styles.chipBody}>
        <Text style={[styles.chipText, { color: colors.textSecondary }]}>
          {translate(schema.label)}
        </Text>
        {phase || level ? (
          <Text style={styles.chipMeta}>
            {phase ? (
              <Text style={{ color: phase.accent }}>
                {elsewhere
                  ? `${translate("Belongs in")} ${translate(phase.label)}`
                  : translate(phase.label)}
              </Text>
            ) : null}
            {phase && level ? (
              <Text style={{ color: colors.textMuted }}> · </Text>
            ) : null}
            {level ? (
              <Text
                style={{
                  color: schema.evidenceWeight > 0 ? colors.success : colors.textMuted,
                }}
              >
                {schema.evidenceWeight > 0 ? `${schema.evidenceWeight}× ` : ""}
                {translate(level.label)}
              </Text>
            ) : null}
          </Text>
        ) : null}
      </View>
    </TouchableOpacity>
  );
};

const BlockPalette = ({ colors, stepPhase = null, onAdd, t }) => {
  const translate = t || ((key) => key);
  return (
    <View style={[styles.palette, { borderColor: colors.border }]}>
      {BLOCK_GROUPS.map((group) => {
        const schemas = BLOCK_SCHEMAS.filter((schema) => schema.group === group.key);
        if (schemas.length === 0) return null;
        return (
          <View key={group.key} style={styles.paletteGroup}>
            <Text style={[styles.paletteLabel, { color: colors.textMuted }]}>
              {translate(group.label)}
            </Text>
            <View style={styles.paletteRow}>
              {schemas.map((schema) => (
                <PaletteChip
                  key={schema.type}
                  colors={colors}
                  schema={schema}
                  stepPhase={stepPhase}
                  onAdd={onAdd}
                  t={t}
                />
              ))}
            </View>
          </View>
        );
      })}

      <Text style={[styles.legend, { color: colors.textMuted }]}>
        {translate(
          "Mastery is weighted: an explanation counts 1.5×, a graded check 1×, an exploration probe 0.5×. Predictions and reflections are recorded but never scored."
        )}
      </Text>
    </View>
  );
};

const styles = StyleSheet.create({
  palette: { borderRadius: 11, borderWidth: 1, borderStyle: "dashed", padding: 12 },
  paletteGroup: { marginBottom: 10 },
  paletteLabel: {
    fontSize: 10,
    fontWeight: "800",
    letterSpacing: 0.5,
    textTransform: "uppercase",
    marginBottom: 6,
  },
  paletteRow: { flexDirection: "row", flexWrap: "wrap", gap: 6 },
  chip: {
    flexDirection: "row",
    alignItems: "center",
    borderRadius: 8,
    borderWidth: 1,
    paddingHorizontal: 9,
    paddingVertical: 6,
    gap: 6,
  },
  chipBody: { minWidth: 0 },
  chipText: { fontSize: 11, fontWeight: "600" },
  chipMeta: { fontSize: 9, fontWeight: "700", marginTop: 1 },
  legend: { fontSize: 10, lineHeight: 15, marginTop: 2 },
});

export default BlockPalette;
